import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { type EventContext, Flow } from "@/instrumentation/logger/types"
import * as amplitude from "@amplitude/analytics-browser"
import { Blockchain } from "@anaconda/core/blockchain/types"
import type { CollectionMiniature } from "@anaconda/core/nft-collection/types"
import { toCurrencyId } from "@anaconda/core/price/types"
import {
  type WhitelistCollectionRequest,
  whitelistCollectionFactoryReatom,
} from "@rarible_int/anaconda_sdk/ethereum"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import Big from "big.js"
import { molecule } from "bunshi"
import { useMolecule } from "bunshi/react"
import { useCallback } from "react"
import type { Address } from "viem"
import { CollectionScope } from "../../anaconda-sdk-scope"
import { BlockchainBalanceSdkProviderMoleculeReatom } from "../../blockchains/blockchain-balance-sdk-provider.reatom"
import { EvmSdkContainerMoleculeReatom } from "../../blockchains/evm-sdk-container-molecule.reatom"
import { InsufficientFundsError } from "../../errors"
import { WhitelistToast } from "../../toasts/whitelist-toast"
import { useSwitchEvmChain } from "../../use-switch-evm-chain"
import {
  TransactionEvent,
  getTransactionErrorMessage,
} from "../events/transaction-event"

type MutationVariables = {
  onSuccess: () => void
}

const RARI_ADDRESS = "0xc61f9663e05fccd84d4d6c56a373093437ecb899"
export const RARI_AMOUNT = 1000
export const WHITELISTING_BLOCKCHAIN = Blockchain.BASE

interface WhitelistActionParams {
  collection: CollectionMiniature
  onSuccess?: () => void
}

interface WhitelistActionResult {
  txId?: string
  collection: CollectionMiniature
}

export function useEvmWhitelisting(props: MutationVariables) {
  const { whitelistAction, collection } = useMolecule(
    WhitelistingTransactionMoleculeReatom,
  )

  const mutate = useCallback(() => {
    whitelistAction({
      collection,
      onSuccess: props.onSuccess,
    })
  }, [whitelistAction, collection, props.onSuccess])

  const onSuccess = useCallback(() => mutate(), [mutate])
  const { action } = useSwitchEvmChain<MutationVariables>(
    // can do whitelist only on BASE
    WHITELISTING_BLOCKCHAIN,
    onSuccess,
  )

  const isPending = whitelistAction.pending() > 0

  return {
    mutate: action,
    isPending,
  }
}

export const WhitelistingTransactionMoleculeReatom = molecule((mol, scope) => {
  const logger = mol(LoggerMolecule)
  const { sdkContainerAtom } = mol(EvmSdkContainerMoleculeReatom)
  const { address: addressAtom } = mol(WalletConnectMoleculeReatom)
  const { balanceSDKAtom } = mol(BlockchainBalanceSdkProviderMoleculeReatom)
  const collection = scope(CollectionScope)

  if (!collection) {
    throw new Error("Collection is not defined for whitelisting molecule")
  }

  const whitelistAction = action(
    async ({ collection, onSuccess }: WhitelistActionParams) => {
      const container = sdkContainerAtom()
      const balanceSDK = balanceSDKAtom()
      const from = addressAtom()

      const { id: collectionId, blockchain } = collection

      if (!from) {
        throw new Error("Wallet is not connected")
      }

      if (!balanceSDK) {
        throw new Error("Balance SDK is not initialized")
      }

      const currentBalance = await wrap(
        balanceSDK.getTokenBalance({
          blockchain: WHITELISTING_BLOCKCHAIN,
          address: from as Address,
          tokenAddress: RARI_ADDRESS,
        }),
      )

      if (currentBalance === "0" || Big(currentBalance).lt(RARI_AMOUNT)) {
        throw new InsufficientFundsError({
          requiredAmount: "0",
          currentBalance: currentBalance,
          currency: {
            id: toCurrencyId(`${WHITELISTING_BLOCKCHAIN}:${RARI_ADDRESS}`),
            abbreviation: "RARI",
          },
          blockchain: WHITELISTING_BLOCKCHAIN,
        })
      }

      // Execute whitelisting via Reatom SDK
      const whitelistCollectionFn = whitelistCollectionFactoryReatom(container)
      const request: WhitelistCollectionRequest = {
        blockchain,
        collectionId,
        from: from as Address,
      }

      const res = await wrap(whitelistCollectionFn(request))
      const { txId } = res

      sonnerToast.dismiss()
      sonnerToast.custom((id) => (
        <WhitelistToast.Success
          collection={collection}
          id={id}
          transactionId={txId}
        />
      ))

      onSuccess?.()

      return { txId, collection }
    },
    "whitelistAction",
  ).extend(
    withAsync(),

    // onCall: Called when action starts (replaces onMutate)
    withCallHook(async (values, [params]) => {
      const { collection } = params

      const baseTransactionEventContext: EventContext = {
        flow: Flow.WhitelistCollection,
        collection: collection.id,
        blockchain: collection.blockchain,
      }

      logger?.api.pushEvent(
        TransactionEvent.Started,
        baseTransactionEventContext,
      )
      amplitude.track(TransactionEvent.Started, baseTransactionEventContext)

      sonnerToast.custom(
        (id) => <WhitelistToast.Pending collection={collection} id={id} />,
        {
          duration: Number.POSITIVE_INFINITY,
        },
      )
    }),
  )

  // onFulfill: Called on success (replaces onSuccess)
  whitelistAction.onFulfill.extend(
    withCallHook((result) => {
      const { txId, collection } = result.payload

      const baseTransactionEventContext: EventContext = {
        flow: Flow.WhitelistCollection,
        collection: collection.id,
        blockchain: collection.blockchain,
      }

      const context = {
        ...baseTransactionEventContext,
        ...(txId && { transactionId: txId }),
      }

      logger?.api.pushEvent(TransactionEvent.Completed, context)
      amplitude.track(TransactionEvent.Completed, context)
    }),
  )

  // onReject: Called on error (replaces onError)
  whitelistAction.onReject.extend(
    withCallHook(({ error, params: [paramsValue] }) => {
      const { collection } = paramsValue

      const baseTransactionEventContext: EventContext = {
        flow: Flow.WhitelistCollection,
        collection: collection.id,
        blockchain: collection.blockchain,
      }

      const context = {
        ...baseTransactionEventContext,
        error: getTransactionErrorMessage(error),
      }

      logger?.api.pushEvent(TransactionEvent.Failed, context)
      amplitude.track(TransactionEvent.Failed, context)

      sonnerToast.dismiss()
      sonnerToast.custom((id) => (
        <WhitelistToast.Error
          collection={collection}
          id={id}
          blockchain={WHITELISTING_BLOCKCHAIN}
          error={error as Error}
        />
      ))
    }),
  )

  return { whitelistAction, collection }
})
